
import React, { useState, useEffect, useRef } from 'react';
import useMediaStore from '../stores/useMediaStore';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';
import { getResourceSignedUrl } from '../services/resourceService';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import workerSrc from '../../node_modules/pdfjs-dist/build/pdf.worker';
import useCourseInstructor from './DeciferInstructorId';
import Hls from 'hls.js';

const VideoPreviewModal = ({ isOpen, onClose, videoId }) => {
    const [videoUrl, setVideoUrl] = useState('');
    const videoRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !videoId) return;
        const fetchUrl = async () => {
            try {
                const res = await axiosClient.get(`/videos/${videoId}/signed-url/auto`);
                const url = res.data?.data?.originalUrl;
                if (!url) return;
                setVideoUrl(url);
            } catch (err) {
                console.error('Failed to get HLS URL', err);
            }
        };
        fetchUrl();
    }, [isOpen, videoId]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoUrl) return;

        let hls = null;
        if (Hls.isSupported() && videoUrl.includes('.m3u8')) {
            hls = new Hls();
            hls.loadSource(videoUrl);
            hls.attachMedia(video);
            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data.fatal) {
                    hls.destroy();
                    if (videoUrl.includes('cloudinary')) {
                        console.warn('HLS failed, falling back to MP4');
                    }
                }
            });
        } else {
            video.src = videoUrl;
        }

        return () => {
            if (hls) hls.destroy();
        };
    }, [videoUrl]);

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
            <div className="relative bg-black p-4 max-w-4xl w-full">
                <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 z-10">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    controlsList="nodownload"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full max-h-[80vh] rounded-lg"
                    onEnded={onClose}
                />
            </div>
        </div>
    );
};
// Resource Preview Modal
const ResourcePreviewModal = ({ isOpen, onClose, resource }) => {
    const [signedUrl, setSignedUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const refreshInterval = useRef(null);
    const toast = useToast();

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const { ZoomIn, ZoomOut, SwitchTheme } = slots;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ZoomOut />
                            <ZoomIn />
                            <SwitchTheme />
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });

    useEffect(() => {
        const fetchSignedUrl = async () => {
            try {
                if (!resource?._id) return;
                const url = await getResourceSignedUrl(resource._id);
                setSignedUrl(url);
            } catch (error) {
                console.error('Failed to fetch signed URL:', error);
                toast.error('Unable to load secure resource');
            }
        };

        if (isOpen) {
            fetchSignedUrl();

            refreshInterval.current = setInterval(fetchSignedUrl, 45000);
        }

        return () => {
            clearInterval(refreshInterval.current);
            setSignedUrl(null);
        };
    }, [isOpen, resource?._id]);

    if (!isOpen || !resource) return null;

    const fileType = resource.type || resource.mimeType || resource.fileType || 'unknown';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
            <div className="relative bg-white dark:bg-neutral-800 p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{resource.originalName
                    }</h3>
                    <button
                        onClick={onClose}
                        className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                {/* Resource Viewer */}
                <div className="bg-black rounded-lg flex items-center justify-center">
                    {!signedUrl ? (
                        <p className="text-gray-300 text-center p-6">Loading secure resource...</p>
                    ) : fileType.includes('image') ? (
                        <img
                            src={signedUrl}
                            alt={resource.title || 'Preview'}
                            className="max-h-[80vh] rounded-lg object-contain"
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                        />
                    ) : fileType.includes('pdf') ? (
                        <div className="w-full h-[80vh] rounded-lg bg-white">
                            <Worker workerUrl={workerSrc}>
                                <Viewer fileUrl={signedUrl} plugins={[defaultLayoutPluginInstance]} />
                            </Worker>
                        </div>
                    ) : (
                        <p className="text-gray-400 text-center p-6">
                            Preview not available for this file type
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

const MediaSelectorModal = ({ isOpen, onClose, onMediaSelect, selectedMedia = [], contentType }) => {
    const {
        fetchVideosForUser: fetchVideos,
        fetchResourcesForUser: fetchResources,
        fetchFoldersForUser: fetchFolders,
        videos,
        resources,
        folders,
        isLoading,
    } = useMediaStore();

    const toast = useToast();

    const initialTab = contentType === 'resource' ? 'resources' : 'videos';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedFolder, setSelectedFolder] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]);
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [encodingStatus, setEncodingStatus] = useState(null);
    const [videoPreview, setVideoPreview] = useState({ isOpen: false, videoId: null });
    const [resourcePreview, setResourcePreview] = useState({ isOpen: false, resource: null });
    const uploadRef = useRef({ uploadId: null, file: null, totalChunks: 0 });

    const instructorId = useCourseInstructor();

    useEffect(() => {
        if (isOpen) {
            const tab = contentType === 'resource' ? 'resources' : 'videos';
            setActiveTab(tab);
            fetchVideos(instructorId);
            fetchResources(instructorId);
            fetchFolders(instructorId);
            setSelectedItems(selectedMedia);
        }
    }, [isOpen, contentType, selectedMedia, instructorId]);

    const checkResumeUpload = async (uploadId) => {
        try {
            const response = await axiosClient.get(`/videos/upload-status/${uploadId}`);
            return response.data.data;
        } catch (error) {
            return null;
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            if (activeTab === 'videos') {
                await handleVideoUpload(file);
            } else {
                await handleResourceUpload(file);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            alert('Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            event.target.value = '';
        }
    };

    const handleVideoUpload = async (file) => {
        const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        uploadRef.current = { uploadId, file };
        const CHUNK_SIZE = 10 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        uploadRef.current.totalChunks = totalChunks;

        let startChunk = 1;
        let videoId = null;
        const resume = await checkResumeUpload(uploadId);
        if (resume?.lastChunk) startChunk = resume.lastChunk + 1;

        for (let i = startChunk; i <= totalChunks; i++) {
            const start = (i - 1) * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);
            const formData = new FormData();
            formData.append('video', chunk);
            formData.append('uploadId', uploadId);
            formData.append('chunkNumber', i);
            formData.append('totalChunks', totalChunks);
            formData.append('fileName', file.name);
            formData.append('uploadedBy', instructorId);
            if (i === 1) {
                formData.append('folder', selectedFolder === 'All' ? 'Uncategorized' : selectedFolder);
                formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
            }
            if (videoId) formData.append('videoId', videoId);

            const res = await axiosClient.post('/videos/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (pe) => {
                    const chunkProg = (pe.loaded / pe.total) * 100;
                    const totalProg = ((i - 1) + chunkProg / 100) / totalChunks * 100;
                    setUploadProgress(Math.round(totalProg));
                }
            });

            if (i === 1 && res.data?.data?.videoId) videoId = res.data.data.videoId;
            if (i === totalChunks) {
                setEncodingStatus('processing');
                const interval = setInterval(async () => {
                    try {
                        const statusRes = await axiosClient.get(`/videos/encoding-status/${videoId}`);
                        const { uploadStatus, encodingProgress } = statusRes.data.data;
                        setUploadProgress(encodingProgress);
                        setEncodingStatus(uploadStatus);
                        if (uploadStatus === 'completed') {
                            clearInterval(interval);
                            fetchVideos();
                            toast.success('Video processed successfully');
                        } else if (uploadStatus === 'failed') {
                            clearInterval(interval);
                            toast.error('Processing failed');
                        }
                    } catch (err) {
                        console.error('Status check error', err);
                    }
                }, 2000);
            }
        }
    };

    const handleResourceUpload = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', selectedFolder === 'All' ? 'Uncategorized' : selectedFolder);
        formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('uploadedBy', instructorId);
        await axiosClient.post('/resources', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                setUploadProgress(percent);
            }
        });
        fetchResources();
    };

    const handleItemSelect = (item, type) => {
        const itemWithMeta = { ...item, type };
        const isSelected = selectedItems.some(selected => selected._id === item._id && selected.type === type);
        if (isSelected) {
            setSelectedItems([]);
        } else {
            setSelectedItems([itemWithMeta]);
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        setIsCreatingFolder(true);
        try {
            const response = await axiosClient.post('/folders', {
                folderPath: newFolderName.trim(),
                uploadedBy: instructorId,
            });
            if (response.data.success) {
                setNewFolderName('');
                const currentFolders = [...folders.filter(f => f !== 'All')];
                if (!currentFolders.includes(newFolderName.trim())) {
                    const updatedFolders = ['All', ...currentFolders, newFolderName.trim()].sort((a, b) =>
                        a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)
                    );
                    useMediaStore.setState({ folders: updatedFolders });
                    setSelectedFolder(newFolderName.trim());
                }
                setTimeout(() => fetchFolders(), 1000);
            }
        } catch (error) {
            console.error('Error creating folder:', error);
        } finally {
            setIsCreatingFolder(false);
        }
    };

    const handleOpenVideoPreview = (e, item) => {
        e.preventDefault();
        setVideoPreview({ isOpen: true, videoId: item._id });
    };

    const handleOpenResourcePreview = async (e, item) => {
        e.preventDefault();

        try {
            const res = await getResourceSignedUrl(item._id);
            const signedUrl = res;

            setResourcePreview({
                isOpen: true,
                resource: {
                    ...item,
                    originalUrl: signedUrl,
                },
            });
        } catch (error) {
            console.error("Error opening resource preview:", error);
            toast.error("Failed to open resource preview");
        }
    };

    const filteredVideos = selectedFolder === 'All'
        ? videos
        : videos.filter(video => video.folder === selectedFolder);

    const filteredResources = selectedFolder === 'All'
        ? resources
        : resources.filter(resource => resource.folder === selectedFolder);

    const searchedVideos = filteredVideos.filter(video =>
        video.originalName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const searchedResources = filteredResources.filter(resource =>
        resource.originalName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const displayedItems = activeTab === 'videos' ? searchedVideos : searchedResources;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-neutral-900 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div
                    className="inline-block align-bottom bg-white dark:bg-neutral-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-headline"
                >
                    {/* Header */}
                    <div className="bg-white dark:bg-neutral-800 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Select Media</h3>
                        <button
                            type="button"
                            className="text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 focus:outline-none"
                            onClick={onClose}
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                        {/* Search and Folder */}
                        <div className="mb-4 flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <input
                                    type="text"
                                    placeholder="Search media..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="flex gap-2">
                                <select
                                    value={selectedFolder}
                                    onChange={(e) => setSelectedFolder(e.target.value)}
                                    className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="All">All Folders</option>
                                    {folders.map(folder => (
                                        <option key={folder} value={folder}>{folder}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    placeholder="New folder"
                                    className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <button
                                    onClick={handleCreateFolder}
                                    disabled={isCreatingFolder}
                                    className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    Create
                                </button>
                            </div>
                        </div>

                        {/* Upload Section */}
                        <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-2">
                                Upload New {activeTab === 'videos' ? 'Video' : 'Resource'}
                            </h4>
                            <label className="flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition">
                                <svg className="mx-auto h-8 w-8 text-neutral-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                                    {isUploading ? 'Uploading...' : `Click to upload ${activeTab === 'videos' ? 'a video' : 'a file'}`}
                                </span>

                                {isUploading && (
                                    <div className="w-full mt-2">
                                        <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${encodingStatus === 'processing'
                                                    ? 'bg-yellow-500 animate-pulse'
                                                    : encodingStatus === 'completed'
                                                        ? 'bg-green-600'
                                                        : 'bg-primary-600'
                                                    }`}
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>

                                        {encodingStatus === 'processing' && (
                                            <p className="text-xs mt-1 text-yellow-600 dark:text-yellow-400 text-center">
                                                Encoding... {uploadProgress.toFixed(1)}%
                                            </p>
                                        )}

                                        {encodingStatus === 'completed' && (
                                            <p className="text-xs mt-1 text-green-600 dark:text-green-400 text-center">
                                                ✅ Encoding completed successfully!
                                            </p>
                                        )}
                                    </div>
                                )}

                                <input
                                    type="file"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                    accept={activeTab === 'videos' ? 'video/*' : '*'}
                                />
                            </label>
                        </div>

                        {/* Media Grid */}
                        {isLoading ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {displayedItems.length > 0 ? (
                                    displayedItems.map(item => {
                                        const isSelected = selectedItems.some(selected => selected._id === item._id);
                                        return (
                                            <div
                                                key={item._id}
                                                onClick={() => handleItemSelect(item, activeTab)}
                                                className={`border rounded-lg p-3 cursor-pointer transition transform hover:scale-105 ${isSelected
                                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                                    : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                                                    }`}
                                            >
                                                <div className="relative">
                                                    {activeTab === 'videos' ? (
                                                        <>
                                                            <div className="aspect-video bg-black rounded mb-2 flex items-center justify-center">
                                                                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => handleOpenVideoPreview(e, item)}
                                                                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-75"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="aspect-video bg-neutral-100 dark:bg-neutral-700 rounded mb-2 flex items-center justify-center">
                                                                <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => handleOpenResourcePreview(e, item)}
                                                                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-75"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                <h3 className="text-sm font-medium text-neutral-900 dark:text-white truncate">{item.originalName}</h3>
                                                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{item.folder}</p>
                                                {activeTab === 'videos' ? (
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{item.duration} sec</p>
                                                ) : (
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{(item.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-8 col-span-full">
                                        <p className="text-neutral-500 dark:text-neutral-400">
                                            No {activeTab} found in this folder.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-neutral-50 dark:bg-neutral-700/50 px-6 py-4 flex justify-end space-x-3">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={selectedItems.length === 0}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            onClick={() => {
                                onMediaSelect(selectedItems);
                                onClose();
                            }}
                        >
                            Select
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview Modals */}
            <VideoPreviewModal
                isOpen={videoPreview.isOpen}
                onClose={() => setVideoPreview({ isOpen: false, videoId: null })}
                videoId={videoPreview.videoId}
            />
            <ResourcePreviewModal
                isOpen={resourcePreview.isOpen}
                onClose={() => setResourcePreview({ isOpen: false, resource: null })}
                resource={resourcePreview.resource}
            />
        </div>
    );
};

export default MediaSelectorModal;